import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { WindowEnums } from 'src/app/enum/window-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { StoreTableModel } from 'src/app/models/tables/archive/store-table.model';
import { StoreService } from 'src/app/services/archive/store.service';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { ImportStoreComponent } from '../import-store/import-store.component';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-store',
  templateUrl: './store.component.html',
  styleUrls: ['./store.component.scss'],
  standalone: false
})
export class StoreComponent implements OnInit {

  @ViewChild('storeImportModal') storeImportModalComponent?: CustomModalComponent | null = null;
  ascendant: boolean = true;
  columnSort: string | null = null;
  data: PagedResponseModel<StoreTableModel> = new PagedResponseModel<StoreTableModel>();
  header: ColumnResponseModel[] = [];
  componentStoreImportModal = ImportStoreComponent;
  filters: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Bodegas';
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }, {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  url: string = 'store';
  userAuthenticated: UserModel = new UserModel();

  constructor(private confirmDialogService: ConfirmDialogService,
    private headerService: HeaderService,
    private optionWindowService: OptionWindowService,
    private router: Router,
    private storeService: StoreService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllStoresWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async delete(store: StoreTableModel) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para eliminar bodegas. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar la bodega?')) {
      try {
        await firstValueFrom(this.storeService.deleteStore(store.storeId));
        await this.getAllStoresWithPagination();
        this.toastHandlerService.showToast(
          'success',
          'La bodega fue eliminada exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar la bodega.', error);
      }
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(
        this.storeService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant)
      );
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Bodegas.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar las bodegas.', error);
    }
  }

  async getAllStoresWithPagination() {
    try {
      this.data = await firstValueFrom(
        this.storeService.getAllStoresWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
    } catch (error) {
      console.error('Error al buscar las bodegas.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.header.length === 0) {
        this.header = await firstValueFrom(this.storeService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general de las bodegas.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getPermissionsForUserByWindow(),
      this.getPermissionProfileByUrl(),
      this.getUserAuthenticated()
    ]);
  }

  async getPermissionsForUserByWindow() {
    try {
      this.permissionsUser = await firstValueFrom(
        this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.Store)
      );
    } catch (error) {
      console.error('Error al cargar los permisos del usuario.', error);
    }
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getUserAuthenticated() {
    try {
      this.userAuthenticated = await firstValueFrom(
        this.userAuthenticatedService.userObservable.pipe(
          filter(user => user && user.userId !== 0)
        )
      );
    } catch (error) {
      console.error('Error al obtener el usuario autenticado.', error);
    }
  }

  newRegister() {
    this.router.navigate([`/${this.url}/register`]);
  }

  onAction(event: { action: string, item?: StoreTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.id}`]);
    }
    if (event.action === 'delete' && event.item) {
      this.delete(event.item);
    }
  }

  async onExport() {
    await this.exportGeneralConsultation();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllStoresWithPagination();
  }

  async onImport() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Importar)) {
      if (this.permissionUpdatable) {
        this.storeImportModalComponent && this.storeImportModalComponent.show();
      } else {
        this.toastHandlerService.showToast(
          'warning',
          `El usuario no tiene permisos para la edición de bodegas. Comuníquese con el administrador del sistema.`
        );
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para importar bodegas, comuniquese con el administrador del sistema.`
      );
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async reloadList(reload: boolean) {
    if (reload) {
      await this.getAllStoresWithPagination();
    }
  }
}
